import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { getMeta, setMeta, META_KEYS } from "../../data/local/db";
import { isAcceptableOffline, type SessionClaims } from "./session";

/**
 * Session state for the terminal.
 *
 * The token is held in IndexedDB rather than localStorage so it survives
 * alongside the offline journal and is cleared by the same wipe.
 *
 * Role checks here are PRESENTATION ONLY. Every mutation is re-authorised
 * server-side on sync (FR-055); this decides what to render, never what is
 * permitted.
 */

export interface AuthState {
  claims: SessionClaims | null;
  token: string | null;
  loading: boolean;
  signIn(staffId: string, pin: string): Promise<void>;
  signOut(): void;
  can(permission: string): boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export class AuthError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "AuthError";
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<SessionClaims | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore a stored session on boot. Must work with no network.
  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const stored = await getMeta<string>(META_KEYS.sessionToken);
      if (!stored) {
        if (!cancelled) setLoading(false);
        return;
      }

      /**
       * Restore by DECODING, not verifying.
       *
       * The signing secret lives only on the server, so the client cannot
       * verify a signature — and should not try. Client-side role checks are
       * presentation only (FR-055); the server re-authorises every mutation on
       * sync, and a forged token buys nothing because the terminal's own
       * records are re-checked there.
       *
       * The earlier version waited for a locally-held verification key that is
       * never written, which signed staff out on every reload. During an
       * outage that would have locked them out of the till entirely — the exact
       * situation Principle I exists to prevent.
       */
      const decoded = decodeClaims(stored);
      if (cancelled) return;

      if (decoded && (decoded.exp * 1000 > Date.now() || isAcceptableOffline(decoded))) {
        setClaims(decoded);
        setToken(stored);
      }
      setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (staffId: string, pin: string) => {
    const response = await fetch("/api/auth/pin", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ staffId, pin }),
    });

    const body = (await response.json()) as
      | { sessionToken: string; staff: { id: string; name: string } }
      | { error: { code: string; message: string } };

    if (!response.ok || "error" in body) {
      const error = "error" in body ? body.error : { code: "INTERNAL", message: "Sign-in failed" };
      throw new AuthError(error.code, error.message);
    }

    const decoded = decodeClaims(body.sessionToken);
    if (!decoded) throw new AuthError("INTERNAL", "Malformed session");

    await setMeta(META_KEYS.sessionToken, body.sessionToken);
    setClaims(decoded);
    setToken(body.sessionToken);
  }, []);

  const signOut = useCallback(() => {
    void setMeta(META_KEYS.sessionToken, null);
    setClaims(null);
    setToken(null);
  }, []);

  const can = useCallback(
    (permission: string) =>
      !!claims && (claims.permissions.includes(permission) || claims.permissions.includes("*")),
    [claims],
  );

  const value = useMemo<AuthState>(
    () => ({ claims, token, loading, signIn, signOut, can }),
    [claims, token, loading, signIn, signOut, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}

/**
 * Reads claims without verifying the signature.
 *
 * That is correct here, not a shortcut: the signing secret is server-only, and
 * the server re-authorises every mutation on sync (FR-055). A tampered token
 * changes what this terminal RENDERS and nothing it can persist.
 */
function decodeClaims(token: string): SessionClaims | null {
  try {
    const body = token.split(".")[1];
    if (!body) return null;
    const padded = body.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
    return JSON.parse(json) as SessionClaims;
  } catch {
    return null;
  }
}
